import { useState } from "react";
import { Link } from "react-router-dom";
import { buttonVariantClass } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Wordmark } from "@/components/Wordmark";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import styles from "./landing.module.css";

export function LandingPage() {
  const { m } = useLocale();
  const lines = m.landing.headline.split("\n");
  const [heroLoaded, setHeroLoaded] = useState(true);

  return (
    <div className={`${styles.page} fade-in`}>
      <a className="skip-link" href="#main">
        {m.common.skipToContent}
      </a>
      <header className={styles.nav}>
        <Wordmark />
        <nav className={styles.navLinks} aria-label={m.landing.navSections}>
          <a href="#workflow">{m.landing.navWorkflow}</a>
          <a href="#preview">{m.landing.navPreview}</a>
          <a href="#capacity">{m.landing.navCapacity}</a>
        </nav>
        <div className={styles.navActions}>
          <LanguageSwitch />
          <ButtonLink to="/login" variant="ghost">
            {m.common.signIn}
          </ButtonLink>
        </div>
      </header>

      <main id="main">
        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroCopy}>
            <h1 id="hero-title" className={styles.headline}>
              {lines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </h1>
            <p className={styles.lead}>{m.landing.lead}</p>
            <div className={styles.ctaRow}>
              <ButtonLink to="/register" variant="primary">
                {m.landing.ctaStart}
              </ButtonLink>
              <a
                href="#preview"
                className={[ui.btn, ui.btnLink, buttonVariantClass("secondary")].join(" ")}
              >
                {m.landing.ctaExplore}
              </a>
            </div>
            <p className={styles.pilotNote}>{m.landing.pilotNote}</p>
          </div>
          <div className={styles.artPanel} aria-label={m.landing.artLabel}>
            {heroLoaded ? (
              <img
                className={styles.artImage}
                src="/assets/hero-mac-studio.png"
                alt={m.landing.artAlt}
                width={1536}
                height={1024}
                decoding="async"
                fetchPriority="high"
                onError={() => setHeroLoaded(false)}
              />
            ) : null}
          </div>
        </section>

        <section id="workflow" className={styles.section} aria-labelledby="workflow-title">
          <h2 id="workflow-title" className={styles.sectionTitle}>
            {m.landing.workflowTitle}
          </h2>
          <p className={styles.sectionLead}>{m.landing.workflowLead}</p>
          <div className={styles.workflow}>
            <div className={styles.workflowRow}>
              <div className={styles.workflowNum}>01</div>
              <div>
                <h3>{m.landing.workflow1Title}</h3>
                <p>{m.landing.workflow1Body}</p>
              </div>
            </div>
            <div className={styles.workflowRow}>
              <div className={styles.workflowNum}>02</div>
              <div>
                <h3>{m.landing.workflow2Title}</h3>
                <p>{m.landing.workflow2Body}</p>
              </div>
            </div>
            <div className={styles.workflowRow}>
              <div className={styles.workflowNum}>03</div>
              <div>
                <h3>{m.landing.workflow3Title}</h3>
                <p>{m.landing.workflow3Body}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="preview" className={styles.section} aria-labelledby="preview-title">
          <h2 id="preview-title" className={styles.sectionTitle}>
            {m.landing.previewTitle}
          </h2>
          <p className={styles.sectionLead}>{m.landing.previewLead}</p>
          <div className={styles.previewShell}>
            <span className={styles.previewBadge}>{m.landing.previewBadge}</span>
            <div className={styles.previewGrid}>
              <div className={styles.previewLane}>
                <h3>{m.landing.previewLane1Title}</h3>
                <p>{m.landing.previewLane1Body}</p>
              </div>
              <div className={styles.previewLane}>
                <h3>{m.landing.previewLane2Title}</h3>
                <p>{m.landing.previewLane2Body}</p>
              </div>
              <div className={styles.previewLane}>
                <h3>{m.landing.previewLane3Title}</h3>
                <p>{m.landing.previewLane3Body}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="capacity" className={styles.section} aria-labelledby="capacity-title">
          <h2 id="capacity-title" className={styles.sectionTitle}>
            {m.landing.capacityTitle}
          </h2>
          <p className={styles.sectionLead}>{m.landing.capacityLead}</p>
          <ul className={styles.capacityList}>
            <li>
              <strong>{m.landing.capacityStorageTitle}</strong>
              <p>{m.landing.capacityStorageBody}</p>
              <span className={styles.capacityMeta}>{m.landing.capacityAvailability}</span>
            </li>
            <li>
              <strong>{m.landing.capacityArchiveTitle}</strong>
              <p>{m.landing.capacityArchiveBody}</p>
              <span className={styles.capacityMeta}>{m.landing.capacityAvailability}</span>
            </li>
            <li>
              <strong>{m.landing.capacityBackupTitle}</strong>
              <p>{m.landing.capacityBackupBody}</p>
              <span className={styles.capacityMeta}>{m.landing.capacityAvailability}</span>
            </li>
          </ul>
        </section>

        <section className={styles.section} aria-labelledby="join-title">
          <div className={styles.bottomCta}>
            <h2 id="join-title" className={styles.sectionTitle}>
              {m.landing.bottomTitle}
            </h2>
            <p className={styles.sectionLead}>{m.landing.bottomLead}</p>
            <div className={styles.ctaRow}>
              <ButtonLink to="/register" variant="primary">
                {m.landing.ctaStart}
              </ButtonLink>
              <ButtonLink to="/login" variant="secondary">
                {m.common.signIn}
              </ButtonLink>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div>
          <Wordmark to="/" />
          <p style={{ margin: "0.65rem 0 0" }}>{m.landing.footerCopy}</p>
        </div>
        <div className={styles.footerLinks}>
          <a href={`https://${m.brand.domain}`}>{m.brand.domain}</a>
          <Link to="/privacy">{m.common.privacy}</Link>
          <Link to="/terms">{m.common.terms}</Link>
          <LanguageSwitch />
        </div>
      </footer>
    </div>
  );
}
